/*
 * The repair that keeps a view's idea of the caret honest, for every binding of this editor.
 *
 * A view learns where the caret went from the document's `selectionchange` event. A browser that
 * does not fire it for a keyboard- or mouse-driven move — headless Chromium, which is what the test
 * estate drives, fires none — would leave every selection-based command (a mark over a range, the
 * link dialog, Enter's split) working on the position before the move. The event is therefore
 * re-dispatched after every key and mouse release: the view's own handler reads the real DOM
 * selection and does nothing when the browser already told it.
 *
 * It is never dispatched during composition. An IME's candidate window owns the selection while it
 * is open, and a view told to re-read it there tears the composition apart.
 */

import type { EditorView } from "prosemirror-view"

export function syncSelection(view: EditorView): void {
	// After the view's own handling of the release: a click's caret is placed by then.
	queueMicrotask(() => {
		if (view.isDestroyed || view.composing) return
		view.dom.ownerDocument.dispatchEvent(new Event("selectionchange"))
	})
}

/**
 * The two DOM events a binding hands to the view for this, as `handleDOMEvents` wants them: each
 * answers false, because the repair adds to the browser's own handling and never replaces it.
 */
export function selectionSyncEvents(): {
	keyup: (view: EditorView) => boolean
	mouseup: (view: EditorView) => boolean
} {
	const after = (view: EditorView) => {
		syncSelection(view)
		return false
	}
	return { keyup: after, mouseup: after }
}
